import React, { useEffect, useRef, useState, useCallback } from "react";
import { useHistory, Link } from "react-router-dom";
import { useAppData } from "../../context/AppDataContext";
import { useAuthInfo } from "../../context/AuthContext";
import Modal from "../modals/Modal";
import ComboBox from "../core/ComboBox";
import TaskDetailDrawer from "../task/TaskDetailDrawer";
import { successfulToast, errorToast } from "../../util/toastNotifications";

const TaskDependencies = ({ task, onTaskUpdated }) => {
  const history = useHistory();
  const { api } = useAppData();
  const { userInfo } = useAuthInfo();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [draftBlockingIds, setDraftBlockingIds] = useState([]);
  const [draftBlockedByIds, setDraftBlockedByIds] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const modalOpenRef = useRef(false);

  const projectId =
    task?.project_id ?? task?.project?.project_id ?? task?.project?.id ?? null;

  const blockedByList = Array.isArray(task?.dependencies)
    ? task.dependencies
    : [];
  const blockingList = Array.isArray(task?.dependants) ? task.dependants : [];

  useEffect(() => {
    if (!api || !userInfo?.org_id) return;
    let cancelled = false;
    (async () => {
      try {
        const allTags = await api.tag.getTags({ org_id: userInfo.org_id });
        const tags = Array.isArray(allTags) ? allTags : [];
        const foundBlocked = tags.find(
          (t) => t.name?.toLowerCase() === "blocked"
        );
        const foundBlocking = tags.find(
          (t) => t.name?.toLowerCase() === "blocking"
        );
        if (!foundBlocked) {
          await api.tag.createTag({
            org_id: userInfo.org_id,
            name: "Blocked",
            color: "#F87171",
          });
        }
        if (!foundBlocking) {
          await api.tag.createTag({
            org_id: userInfo.org_id,
            name: "Blocking",
            color: "#FBBF24",
          });
        }
      } catch (_) {
        if (!cancelled) {
          /* tags used for list badges; backend sync still updates edges */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, userInfo?.org_id]);

  useEffect(() => {
    if (!api || !projectId || !task?.task_id) return;
    let cancelled = false;
    setIsLoadingTasks(true);
    api.task
      .getTasks({
        include: ["status", "tags"],
        project_id: projectId,
      })
      .then((tasks) => {
        if (cancelled) return;
        const list = Array.isArray(tasks) ? tasks : [];
        setAvailableTasks(list.filter((t) => t.status !== "Complete"));
      })
      .catch(() => {
        if (!cancelled) setAvailableTasks([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTasks(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, projectId, task?.task_id]);

  useEffect(() => {
    if (isModalOpen) {
      if (!modalOpenRef.current && task) {
        const deps = task.dependencies || [];
        const dents = task.dependants || [];
        setDraftBlockedByIds(deps.map((d) => d.task_id).filter(Boolean));
        setDraftBlockingIds(dents.map((d) => d.task_id).filter(Boolean));
      }
      modalOpenRef.current = true;
    } else {
      modalOpenRef.current = false;
    }
  }, [isModalOpen, task, task?.task_id, task?.dependencies, task?.dependants]);

  if (!task) return null;

  const handleOpenTaskLink = (taskId) => {
    if (taskId) history.push(`/task/${taskId}`);
  };

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
    setIsDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTaskId(null);
  };

  const handleSave = async () => {
    if (!api || !task?.task_id) return;
    const overlap = draftBlockedByIds.find((id) =>
      draftBlockingIds.includes(id)
    );
    if (overlap) {
      errorToast(
        "A task cannot be both blocked by and blocking the same related task."
      );
      return;
    }
    try {
      await api.task.updateTaskDependencies(task.task_id, {
        dependency_task_ids: draftBlockedByIds,
        dependant_task_ids: draftBlockingIds,
      });

      successfulToast("Dependencies updated successfully");

      if (projectId) {
        const tasks = await api.task.getTasks({
          include: ["status", "tags"],
          project_id: projectId,
        });
        const list = Array.isArray(tasks) ? tasks : [];
        setAvailableTasks(list.filter((t) => t.task_id !== task.task_id));
      }
      const updatedTask = await api.task.getTaskById(task.task_id);
      if (onTaskUpdated) onTaskUpdated(updatedTask);
      setIsModalOpen(false);
    } catch (err) {
      const msg =
        err?.message ||
        (typeof err === "string" ? err : null) ||
        "Failed to update dependencies";
      errorToast(msg);
    }
  };

  const handleToggleDraftBlocking = (taskId) => {
    setDraftBlockedByIds((prev) => prev.filter((id) => id !== taskId));
    setDraftBlockingIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleToggleDraftBlockedBy = (taskId) => {
    setDraftBlockingIds((prev) => prev.filter((id) => id !== taskId));
    setDraftBlockedByIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const formatTaskOption = (candidateTask) => {
    if (!candidateTask || typeof candidateTask !== "object") return "";
    const name = candidateTask.name ?? "";
    return name;
  };

  const safeAvailableTasks = Array.isArray(availableTasks)
    ? availableTasks
    : [];
  const hasAnyDependencies =
    blockedByList.length > 0 || blockingList.length > 0;
  const canEdit = Boolean(projectId && task.task_id);

  return (
    <div className="task-dependencies info-item">
      <span className="info-label">Blocked/Blocking</span>
      <div className="info-value task-dependencies-content">
        {hasAnyDependencies ? (
          <div className="task-dependencies-tags">
            {blockedByList.length > 0 && (
              <div className="task-dependency-row">
                <span className="dependency-label">Blocked by:</span>
                <div className="dependency-links">
                  {blockedByList.map((dep) => (
                    <button
                      key={dep.task_id}
                      type="button"
                      className="link-button"
                      onClick={() => handleOpenTaskLink(dep.task_id)}
                    >
                      {dep.name ?? dep.task_id}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {blockingList.length > 0 && (
              <div className="task-dependency-row">
                <span className="dependency-label">Blocking:</span>
                <div className="dependency-links">
                  {blockingList.map((dep) => (
                    <button
                      key={dep.task_id}
                      type="button"
                      className="link-button"
                      onClick={() => handleOpenTaskLink(dep.task_id)}
                    >
                      {dep.name ?? dep.task_id}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <span className="dependency-none">No blocking or blocked tasks</span>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-sm ml-2 small"
          onClick={() => setIsModalOpen(true)}
          disabled={!canEdit}
        >
          Edit
        </button>
      </div>

      <Modal
        isModalOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        overlay={{
          zIndex: 10060,
          backdropFilter: "blur(3px)",
          backgroundColor: "rgba(15, 15, 18, 0.72)",
        }}
        content={{
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          margin: 0,
          transform: "translate(-50%, -50%)",
          width: "min(1100px, calc(100vw - 40px))",
          maxWidth: "min(1100px, calc(100vw - 40px))",
          maxHeight: "min(90vh, 960px)",
          height: "auto",
          minHeight: "min(62vh, 560px)",
          padding: 0,
          border: "none",
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "transparent",
          boxShadow:
            "0 24px 48px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.06)",
        }}
      >
        <div className="modal-content task-dependencies-modal">
          <div className="modal-header">
            <h3>Manage Blocking / Blocked Tasks</h3>
            <button
              type="button"
              className="close"
              onClick={() => setIsModalOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            {!projectId ? (
              <p className="text-muted">
                Assign a project to this task to manage blocking and blocked
                tasks.
              </p>
            ) : isLoadingTasks ? (
              <div className="loading">Loading tasks...</div>
            ) : (
              <div className="task-dependencies-modal__grid">
                <div className="dependencies-section">
                  <h4>Tasks this task is blocked by</h4>
                  <p className="text-muted">
                    Select tasks that must be completed before this task can
                    proceed. They will get a &quot;Blocking&quot; tag.
                  </p>
                  <div className="dependencies-section__stretch">
                    <div className="dependencies-section__picker">
                      <ComboBox
                        data={safeAvailableTasks}
                        selected={safeAvailableTasks.filter((t) =>
                          draftBlockedByIds.includes(t.task_id)
                        )}
                        valueKey="task_id"
                        placeholder="Search tasks..."
                        searchTopic="task"
                        formatData={formatTaskOption}
                        handleSelect={handleToggleDraftBlockedBy}
                        onRequestClose={() => {}}
                        inline={false}
                        allowMultiple={true}
                      />
                    </div>
                    <div className="selected-dependencies-list">
                      {draftBlockedByIds.length === 0 && (
                        <span className="text-muted">
                          No blocking tasks selected.
                        </span>
                      )}
                      {draftBlockedByIds.map((tid) => {
                        const dep = safeAvailableTasks.find(
                          (t) => t.task_id === tid
                        );
                        if (!dep) return null;
                        return (
                          <div
                            key={dep.task_id}
                            className="flex-rows gap-10 flex-align-center"
                          >
                            <button
                              type="button"
                              onClick={() => handleTaskClick(dep.task_id)}
                            >
                              {dep.name}
                            </button>
                            <button
                              type="button"
                              className="small"
                              onClick={() =>
                                handleToggleDraftBlockedBy(dep.task_id)
                              }
                              aria-label="Remove"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="dependencies-section dependencies-section--blocking">
                  <h4>Tasks this task is blocking</h4>
                  <p className="text-muted">
                    Select tasks that are waiting on this task. They will get a
                    &quot;Blocked&quot; tag.
                  </p>
                  <div className="dependencies-section__stretch">
                    <div className="dependencies-section__picker">
                      <ComboBox
                        data={safeAvailableTasks}
                        selected={safeAvailableTasks.filter((t) =>
                          draftBlockingIds.includes(t.task_id)
                        )}
                        valueKey="task_id"
                        placeholder="Search tasks..."
                        searchTopic="task"
                        formatData={formatTaskOption}
                        handleSelect={handleToggleDraftBlocking}
                        onRequestClose={() => {}}
                        inline={false}
                        allowMultiple={true}
                      />
                    </div>
                    <div className="selected-dependencies-list">
                      {draftBlockingIds.length === 0 && (
                        <span className="text-muted">
                          No tasks selected as being blocked by this task.
                        </span>
                      )}
                      {draftBlockingIds.map((tid) => {
                        const dep = safeAvailableTasks.find(
                          (t) => t.task_id === tid
                        );
                        if (!dep) return null;
                        return (
                          <div
                            key={dep.task_id}
                            className="flex-rows gap-10 flex-align-center"
                          >
                            <button
                              type="button"
                              onClick={() => handleTaskClick(dep.task_id)}
                            >
                              {dep.name}
                            </button>

                            <button
                              type="button"
                              className="small"
                              onClick={() =>
                                handleToggleDraftBlocking(dep.task_id)
                              }
                              aria-label="Remove"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <TaskDetailDrawer
              taskId={selectedTaskId}
              isOpen={isDrawerOpen}
              onClose={handleCloseDrawer}
            />
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isLoadingTasks || !projectId}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TaskDependencies;
